/**
 * Minimal cluster-detail page mounted at /clusters/:cluster_id.
 *
 * The route was missing before the audit; MatchCard's "Open" button and
 * the Realtime toast "Open cluster" action both navigate here. Without
 * this page they dead-ended at NotFoundPage.
 *
 * Renders representative title + summary + member headlines + category +
 * affected materials. The trading dashboard has its own richer cluster
 * surface (in src/trading/), but linking that from a notification flow is
 * out of scope here — this page is the universal landing pad.
 */
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ClusterMember {
  article_id: string;
  joined_at?: string;
  title?: string;
  url?: string;
  feed_id?: string | null;
}

interface ClusterCategory {
  category_id: string;
  score: number;
  is_primary: boolean;
}

interface ClusterMaterial {
  material_id: string;
  relevance_score: number;
  impact_channel?: string | null;
  direction_hint?: string | null;
  horizon_hint?: string | null;
  reason?: string | null;
}

interface ClusterDetail {
  id: string;
  representative_article_id?: string;
  first_seen_at?: string;
  last_seen_at?: string;
  primary_category?: string;
  members?: ClusterMember[];
  categories?: ClusterCategory[];
  material_impacts?: ClusterMaterial[];
  // Backend returns a wider shape (enrichment fields, observations, etc.);
  // narrowing to what this page renders. The rest is ignored.
  [key: string]: unknown;
}

export function ClusterDetailPage() {
  const { cluster_id = "" } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery<ClusterDetail>({
    queryKey: ["cluster", cluster_id],
    queryFn: () => apiFetch<ClusterDetail>(`/api/v1/clusters/${cluster_id}`),
    enabled: Boolean(cluster_id),
  });

  if (isLoading) {
    return <div className="p-6">Loading cluster…</div>;
  }
  if (isError) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-3">
        <button
          className="text-sm text-muted-foreground"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <p className="text-red-600">
          Couldn&apos;t load this cluster.{" "}
          <span className="text-muted-foreground">
            {error instanceof Error ? error.message : ""}
          </span>
        </p>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6">Cluster not found.</div>;
  }

  const representativeTitle =
    data.members?.find((m) => m.article_id === data.representative_article_id)?.title ??
    (data.members?.[0]?.title ?? "(no title)");
  const memberCount = data.members?.length ?? 0;
  const categories = data.categories ?? [];
  const materials = data.material_impacts ?? [];

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <button
        className="text-sm text-muted-foreground"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div>
        <h1 className="text-xl font-semibold">{representativeTitle}</h1>
        <p className="text-xs text-muted-foreground mt-1">
          {memberCount} member article{memberCount === 1 ? "" : "s"}
          {data.first_seen_at ? ` · first seen ${new Date(data.first_seen_at).toLocaleString()}` : ""}
        </p>
      </div>

      {categories.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              Categories
            </h3>
            <div className="flex flex-wrap gap-1">
              {categories.map((c) => (
                <Badge key={c.category_id} variant={c.is_primary ? "default" : "outline"}>
                  {c.category_id}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {materials.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              Affected materials
            </h3>
            <ul className="space-y-1 text-sm">
              {materials.map((m) => (
                <li key={m.material_id}>
                  <strong>{m.material_id}</strong>
                  <span className="text-muted-foreground">
                    {" "}
                    · relevance {m.relevance_score.toFixed(2)}
                    {m.direction_hint ? ` · ${m.direction_hint}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Member articles ({memberCount})
          </h3>
          <ul className="space-y-2 text-sm">
            {data.members?.map((m) => (
              <li key={m.article_id} className="border-s-2 ps-3 border-border">
                <div className="font-medium">
                  {m.url ? (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {m.title ?? "(no title)"}
                    </a>
                  ) : (
                    m.title ?? "(no title)"
                  )}
                </div>
                {m.joined_at && (
                  <div className="text-xs text-muted-foreground">
                    joined {new Date(m.joined_at).toLocaleString()}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="pt-2">
        <Button variant="outline" asChild>
          <Link to="/monitors">← All monitors</Link>
        </Button>
      </div>
    </div>
  );
}
